import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RotateCw, Maximize2, Minimize2, ZoomIn, ZoomOut } from 'lucide-react';
import './SharePage.css';
import { shareService, SharedPhotoMetadata } from '../../../services/ShareService';
import { getShareImageUrl } from '../../../utils/urlBuilder';
import { useLanguage } from '../../../contexts/LanguageContext';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('SharePage');

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

export default function SharePage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useLanguage();
  const [metadata, setMetadata] = useState<SharedPhotoMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  // View-only transforms, same behavior as PhotoDetailModal's toolbar (rotate + full view),
  // plus zoom/pan which the dialog doesn't have.
  const [rotation, setRotation] = useState(0);
  const [isFullView, setIsFullView] = useState(false);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  const handleRotate = () => setRotation(r => (r + 90) % 360);

  const applyZoom = (next: number) => {
    const clamped = clampZoom(next);
    setZoom(clamped);
    if (clamped === MIN_ZOOM) {
      setPan({ x: 0, y: 0 });
    }
  };

  const handleZoomIn = () => applyZoom(zoom + ZOOM_STEP);
  const handleZoomOut = () => applyZoom(zoom - ZOOM_STEP);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    applyZoom(zoom + (e.deltaY < 0 ? ZOOM_STEP / 2 : -ZOOM_STEP / 2));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    if (zoom <= MIN_ZOOM) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!dragState.current) return;
    const { startX, startY, startPanX, startPanY } = dragState.current;
    setPan({ x: startPanX + (e.clientX - startX), y: startPanY + (e.clientY - startY) });
  };

  const handlePointerUp = () => {
    dragState.current = null;
    setIsDragging(false);
  };

  useEffect(() => {
    if (!token) {
      setNotAvailable(true);
      setLoading(false);
      return;
    }

    const fetchSharedPhoto = async () => {
      try {
        const data = await shareService.getSharedPhoto(token);
        setMetadata(data);
      } catch (err) {
        logger.debug('Shared photo unavailable:', err);
        setNotAvailable(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSharedPhoto();
  }, [token]);

  if (loading) {
    return (
      <div className="share-page share-page-center">
        <p>{t('share.loading')}</p>
      </div>
    );
  }

  if (notAvailable || !token) {
    return (
      <div className="share-page share-page-center">
        <h1>{t('share.unavailableTitle')}</h1>
        <p>{t('share.unavailableMessage')}</p>
      </div>
    );
  }

  return (
    <div className={`share-page ${isFullView ? 'fullview' : ''}`}>
      <div className="share-page-toolbar">
        <button
          className="share-page-toolbar-btn"
          onClick={handleZoomOut}
          disabled={zoom <= MIN_ZOOM}
          title="Zoom out"
          aria-label="Zoom out"
        >
          <ZoomOut size={20} strokeWidth={1.5} />
        </button>
        <button
          className="share-page-toolbar-btn"
          onClick={handleZoomIn}
          disabled={zoom >= MAX_ZOOM}
          title="Zoom in"
          aria-label="Zoom in"
        >
          <ZoomIn size={20} strokeWidth={1.5} />
        </button>
        <button
          className="share-page-toolbar-btn"
          onClick={handleRotate}
          title="Rotate"
          aria-label="Rotate photo"
        >
          <RotateCw size={20} strokeWidth={1.5} />
        </button>
        <button
          className="share-page-toolbar-btn"
          onClick={() => setIsFullView(v => !v)}
          title={isFullView ? 'Exit full view' : 'Full view'}
          aria-label="Toggle full view"
        >
          {isFullView ? <Minimize2 size={20} strokeWidth={1.5} /> : <Maximize2 size={20} strokeWidth={1.5} />}
        </button>
      </div>

      <div className="share-page-image-container" onWheel={handleWheel}>
        <img
          className={`share-page-image ${isDragging ? 'dragging' : ''}`}
          src={getShareImageUrl(token)}
          alt="Shared family moment"
          draggable={false}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            cursor: zoom > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default',
            touchAction: zoom > MIN_ZOOM ? 'none' : 'pinch-zoom',
          }}
        />
      </div>
      {!isFullView && metadata && metadata.tagsSnapshot.length > 0 && (
        <div className="share-page-tags">
          <span className="share-page-tags-label">{t('share.tagged')}:</span>
          {metadata.tagsSnapshot.map((tag) => (
            <span key={tag} className="share-page-tag-chip">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}
